import type { CsvRow } from "../types/draft";

// ── Cross-platform player identity ────────────────────────────────
//
// Underdog and DraftKings each name players their own way. Joining a mixed
// portfolio on the raw "First Last" string splits the same human into two
// entries — which is what the visualizer used to do. Measured against the two
// live boards (UD 1,426 players / DK 463), the raw string disagrees for ~7% of
// DK's pool. The disagreements come in four flavours:
//
//   1. Generational suffix    DK "Marvin Harrison Jr."  UD "Marvin Harrison"
//   2. Punctuation            DK "DJ Moore"             UD "D.J. Moore"
//   3. Given name vs nickname DK "Kenneth Gainwell"     UD "Kenny Gainwell"
//                             DK "Nick Singleton"       UD "Nicholas Singleton"
//   4. Stale team/position    DK "Nick Chubb" (FA)      UD "Nick Chubb" (HOU)
//                             UD "Eli Heidenreich" (RB) DK same player (WR)
//
// (1)–(3) are handled by `canonicalName`. (4) is why team and position are NOT
// part of the identity key: across the combined 2,272-row pool every single
// name whose team/position disagreed across sources was the SAME human with one
// side stale — and there were zero cases of two different draftable players
// sharing a canonical name. Team and position are therefore treated as
// *attributes* to be reconciled (freshest pick wins), not as identity.

// ── Nickname roots ────────────────────────────────────────────────
// Given name and its common short forms collapse to one root, applied to the
// FIRST name only. Adding this layer to the combined UD+DK pool merged 3 extra
// keys and introduced zero new collisions, so it is safe as a blanket rule.
const NICKNAME_ROOTS: Record<string, string> = {
  kenneth: "ken", kenny: "ken", ken: "ken",
  nicholas: "nick", nick: "nick", nicky: "nick",
  michael: "mike", mike: "mike", mikey: "mike",
  christopher: "chris", chris: "chris",
  joshua: "josh", josh: "josh",
  matthew: "matt", matt: "matt", matty: "matt",
  benjamin: "ben", ben: "ben", benji: "ben",
  william: "will", will: "will", bill: "will", billy: "will", willie: "will",
  robert: "rob", rob: "rob", bob: "rob", bobby: "rob", robbie: "rob",
  zachary: "zach", zach: "zach", zack: "zach",
  jonathan: "jon", johnathan: "jon", jon: "jon", john: "jon", johnny: "jon",
  nathaniel: "nate", nathan: "nate", nate: "nate",
  gabriel: "gabe", gabe: "gabe",
  samuel: "sam", sam: "sam", sammy: "sam",
  daniel: "dan", dan: "dan", danny: "dan",
  timothy: "tim", tim: "tim",
  anthony: "tony", tony: "tony",
  cameron: "cam", cam: "cam",
  patrick: "pat", pat: "pat",
  joseph: "joe", joe: "joe", joey: "joe",
  jeffrey: "jeff", jeff: "jeff",
  gregory: "greg", greg: "greg",
  andrew: "drew", drew: "drew", andy: "drew",
  thomas: "tom", tom: "tom", tommy: "tom",
  richard: "rick", rick: "rick", ricky: "rick",
  edward: "ed", ed: "ed", eddie: "ed",
  charles: "charlie", charlie: "charlie", chuck: "charlie",
  stephen: "steve", steven: "steve", steve: "steve",
  david: "dave", dave: "dave",
  alexander: "alex", alex: "alex",
  jacob: "jake", jake: "jake",
  frederick: "fred", fred: "fred", freddie: "fred",
  raymond: "ray", ray: "ray",
  ronald: "ron", ron: "ron", ronnie: "ron",
  donald: "don", don: "don", donnie: "don",
  isiah: "isaiah", isaiah: "isaiah",
};

// Trailing generational suffixes, as their own token.
const SUFFIX_RE = /\s+(jr|sr|ii|iii|iv|v)$/;

/**
 * The single join key for a player across platforms, feeds and files.
 *
 * Lowercases, strips diacritics, drops punctuation (periods, apostrophes),
 * folds hyphens to spaces, removes a trailing generational suffix, then maps a
 * recognised given name to its nickname root.
 *
 *   "Marvin Harrison Jr."  → "marvin harrison"
 *   "D.J. Moore"           → "dj moore"
 *   "JuJu Smith-Schuster"  → "juju smith schuster"
 *   "Kenneth Gainwell"     → "ken gainwell"   (= "Kenny Gainwell")
 *   "Nicholas Singleton"   → "nick singleton" (= "Nick Singleton")
 */
export function canonicalName(raw: string): string {
  const base = (raw || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[.'`’]/g, "")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(SUFFIX_RE, "")
    .trim();
  if (!base) return "";
  const space = base.indexOf(" ");
  if (space === -1) return base;
  const root = NICKNAME_ROOTS[base.slice(0, space)];
  return root ? `${root}${base.slice(space)}` : base;
}

// ── Team abbreviations ────────────────────────────────────────────
// Mirrors src/utils/team-abbr.ts in the extension. A CSV uploaded by hand (or
// exported from a third-party tool) can carry any of these variants, and the
// visualizer previously only knew WSH→WAS.
const TEAM_ALIASES: Record<string, string> = {
  WSH: "WAS", WFT: "WAS",
  JAC: "JAX",
  LVR: "LV", OAK: "LV", LAS: "LV",
  LA: "LAR", STL: "LAR", RAM: "LAR",
  SD: "LAC", SDG: "LAC",
  ARZ: "ARI",
  GNB: "GB", KAN: "KC", NWE: "NE", NOR: "NO", SFO: "SF", TAM: "TB",
  CLV: "CLE", BLT: "BAL", HST: "HOU",
};

/** Canonical NFL abbreviation. "FA" and blanks both mean "no team known". */
export function canonicalTeam(raw: string | null | undefined): string {
  if (!raw) return "";
  const up = raw.trim().toUpperCase();
  if (up === "FA" || up === "NONE") return "";
  return TEAM_ALIASES[up] ?? up;
}

// ── Portfolio-wide reconciliation ─────────────────────────────────

/** One player's reconciled identity, shared by every row that refers to them. */
export interface ResolvedIdentity {
  /** Join key — `canonicalName` of the player. */
  key: string;
  /** Display name (e.g. "Marvin Harrison Jr."). */
  name: string;
  firstName: string;
  lastName: string;
  team: string;
  position: string;
}

/** Most frequent non-blank value; ties broken by whichever appeared on the most
 *  recent pick, so a trade or a position reclass resolves to the fresher side. */
function reconcile(entries: Array<{ value: string; at: string }>): string {
  const counts = new Map<string, { n: number; latest: string }>();
  for (const { value, at } of entries) {
    if (!value) continue;
    const cur = counts.get(value);
    if (cur) {
      cur.n += 1;
      if (at > cur.latest) cur.latest = at;
    } else {
      counts.set(value, { n: 1, latest: at });
    }
  }
  let best = "";
  let bestN = 0;
  let bestAt = "";
  for (const [value, { n, latest }] of counts) {
    if (n > bestN || (n === bestN && latest > bestAt)) {
      best = value;
      bestN = n;
      bestAt = latest;
    }
  }
  return best;
}

/**
 * Build the canonical identity for every player in the portfolio.
 *
 * Rows are bucketed by `canonicalName`, so a player drafted as "Kenneth
 * Gainwell" on DraftKings and "Kenny Gainwell" on Underdog lands in one bucket.
 * Display name, team and position are then reconciled across the bucket.
 */
export function buildIdentityRegistry(rows: CsvRow[]): Map<string, ResolvedIdentity> {
  const buckets = new Map<string, {
    names: Array<{ value: string; at: string }>;
    teams: Array<{ value: string; at: string }>;
    positions: Array<{ value: string; at: string }>;
  }>();

  for (const row of rows) {
    const first = (row["First Name"] || "").trim();
    const last = (row["Last Name"] || "").trim();
    const display = `${first} ${last}`.trim();
    const key = canonicalName(display);
    if (!key) continue;
    const at = row["Picked At"] || "";
    let b = buckets.get(key);
    if (!b) {
      b = { names: [], teams: [], positions: [] };
      buckets.set(key, b);
    }
    b.names.push({ value: display, at });
    b.teams.push({ value: canonicalTeam(row["Team"]), at });
    b.positions.push({ value: (row["Position"] || "").trim(), at });
  }

  const registry = new Map<string, ResolvedIdentity>();
  for (const [key, b] of buckets) {
    const name = reconcile(b.names);
    const space = name.indexOf(" ");
    registry.set(key, {
      key,
      name,
      firstName: space === -1 ? name : name.slice(0, space),
      lastName: space === -1 ? "" : name.slice(space + 1),
      team: reconcile(b.teams),
      position: reconcile(b.positions),
    });
  }
  return registry;
}

/**
 * Rewrite a portfolio's rows so every reference to the same human carries the
 * same name, team and position — the single place cross-platform duplicates are
 * collapsed. Applied once, upstream of every derived surface (exposures, combos,
 * stacks, archetypes, charts), so none of them need to know about the problem.
 *
 * Rows that already match their resolved identity are returned by reference, so
 * a single-platform portfolio allocates nothing.
 */
export function resolveIdentities(rows: CsvRow[]): CsvRow[] {
  if (rows.length === 0) return rows;
  const registry = buildIdentityRegistry(rows);
  let changed = false;
  const out = rows.map(row => {
    const key = canonicalName(`${row["First Name"] || ""} ${row["Last Name"] || ""}`.trim());
    const id = key ? registry.get(key) : undefined;
    if (!id) return row;
    if (
      row["First Name"] === id.firstName &&
      row["Last Name"] === id.lastName &&
      (row["Team"] || "") === id.team &&
      (row["Position"] || "") === id.position
    ) {
      return row;
    }
    changed = true;
    return {
      ...row,
      "First Name": id.firstName,
      "Last Name": id.lastName,
      Team: id.team,
      Position: id.position,
    };
  });
  return changed ? out : rows;
}
